/**
 * Businesses window: install facilities, staff them, run production, price
 * shop offers — plus the direct property offers window.
 */

import type { FacilitySummary, PropertyOfferSummary } from "@parcelbound/contracts";
import { useEffect, useMemo, useRef, useState } from "react";
import type { JSX } from "react";
import {
  api,
  ApiError,
  type BusinessCatalogResponse,
  type LogisticsResponse,
  type PortfolioResponse,
} from "./api";
import { useApp, type WindowId } from "./store";
import { Window } from "./Window";

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : "something went wrong";
}

function money(wire: string): string {
  const n = Number(wire);
  if (!Number.isFinite(n)) return wire;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function useWindowChrome(id: WindowId) {
  const focused = useApp((s) => s.focused) === id;
  const closeWindow = useApp((s) => s.closeWindow);
  const focusWindow = useApp((s) => s.focusWindow);
  return { focused, onClose: () => closeWindow(id), onFocus: () => focusWindow(id) };
}

const STALL_LABELS: Record<string, string> = {
  insufficient_cash: "out of cash",
  missing_inputs: "waiting for inputs",
  output_full: "output storage full",
  no_staff: "nobody working",
  no_permit: "no permit",
  unpaid_rent: "rent unpaid",
};

export function BusinessWindow(): JSX.Element {
  const chrome = useWindowChrome("business");
  const notify = useApp((s) => s.notify);
  const [tab, setTab] = useState<"facilities" | "install" | "logistics">("facilities");
  const [catalog, setCatalog] = useState<BusinessCatalogResponse | null>(null);
  const [facilities, setFacilities] = useState<FacilitySummary[] | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = async (): Promise<void> => {
    try {
      const r = await api.facilities();
      setFacilities(r.facilities);
    } catch (err) {
      notify("err", describe(err));
    }
  };

  useEffect(() => {
    api.businessCatalog().then(setCatalog, (e) => notify("err", describe(e)));
    void reload();
    // Production and sales settle continuously; keep the numbers moving.
    const t = setInterval(() => void reload(), 15_000);
    return () => clearInterval(t);
  }, []);

  const act = async (fn: () => Promise<string>): Promise<void> => {
    setBusy(true);
    try {
      notify("ok", await fn());
      await reload();
      useApp.getState().setDashboard(await api.dashboard());
    } catch (err) {
      notify("err", describe(err));
    } finally {
      setBusy(false);
    }
  };

  const tabs = (
    <div className="tabs" role="tablist">
      <button role="tab" aria-selected={tab === "facilities"} onClick={() => setTab("facilities")}>
        Facilities <span className="count">{facilities?.length ?? 0}</span>
      </button>
      <button role="tab" aria-selected={tab === "install"} onClick={() => setTab("install")}>
        Install
      </button>
      <button role="tab" aria-selected={tab === "logistics"} onClick={() => setTab("logistics")}>
        Logistics
      </button>
    </div>
  );

  return (
    <Window
      title="Businesses"
      initial={{ x: 200, y: 110 }}
      width={560}
      maxHeight={620}
      tabs={tabs}
      {...chrome}
    >
      {tab === "logistics" ? (
        <LogisticsSection catalog={catalog} facilities={facilities ?? []} busy={busy} act={act} />
      ) : tab === "install" ? (
        <InstallSection catalog={catalog} busy={busy} act={act} />
      ) : facilities === null ? (
        <p className="empty">Loading…</p>
      ) : facilities.length === 0 ? (
        <p className="empty">
          No businesses yet. Install a facility — your food-cart permit makes the cart the cheapest
          start, or put a farm on owned land and a workshop in a unit you own or lease.
        </p>
      ) : (
        facilities.map((f) => (
          <FacilityCard key={f.id} facility={f} catalog={catalog} busy={busy} act={act} />
        ))
      )}
    </Window>
  );
}

function FacilityCard({
  facility: f,
  catalog,
  busy,
  act,
}: {
  facility: FacilitySummary;
  catalog: BusinessCatalogResponse | null;
  busy: boolean;
  act: (fn: () => Promise<string>) => Promise<void>;
}): JSX.Element {
  const orders = useMemo(
    () => (catalog?.workOrders ?? []).filter((o) => o.facility === f.facilityType),
    [catalog, f.facilityType],
  );
  const [orderId, setOrderId] = useState(f.job?.workOrderId ?? "");
  const [offerCommodity, setOfferCommodity] = useState("");
  const [offerPrice, setOfferPrice] = useState("1.00");

  useEffect(() => {
    setOrderId(f.job?.workOrderId ?? "");
  }, [f.job?.workOrderId]);

  const paused = f.status === "paused";

  return (
    <div className="card">
      <div className="kv">
        <span className="k" style={{ fontWeight: 700, color: "var(--ink)" }}>
          {f.typeName}
        </span>
        <span className={`pill-tag${paused ? "" : " mine"}`}>
          {paused ? "paused" : f.stallReason ? (STALL_LABELS[f.stallReason] ?? f.stallReason) : "running"}
        </span>
      </div>
      <div className="kv">
        <span className="k">Costs / game day</span>
        <span className="v">
          {money(f.upkeepPerDay)} upkeep · {money(f.wagesPerDay)} wages
        </span>
      </div>
      <div className="kv">
        <span className="k">Storage</span>
        <span className="v">
          {f.storageUsed.toLocaleString()} / {f.storageUnits.toLocaleString()} units
        </span>
      </div>
      {f.stock.length > 0 && (
        <div className="kv">
          <span className="k">In stock</span>
          <span className="v small">
            {f.stock.map((s) => `${s.qty} ${s.commodity}`).join(" · ")}
          </span>
        </div>
      )}
      {f.facilityType === "barn" && (
        <HerdSection facility={f} busy={busy} act={act} />
      )}

      {paused && (
        <div className="row-actions">
          <button
            className="primary"
            disabled={busy}
            onClick={() =>
              void act(async () => {
                await api.resumeFacility(f.id);
                return `${f.typeName} resumed${f.reactivationCost ? ` for ${money(f.reactivationCost)}` : ""}.`;
              })
            }
          >
            Resume{f.reactivationCost ? ` for ${money(f.reactivationCost)}` : ""}
          </button>
        </div>
      )}

      <div className="kv">
        <span className="k">
          Staff {f.workers.length}/{f.workerSlots}
        </span>
        <span className="v">
          {f.workers.length < f.workerSlots && (
            <button
              disabled={busy}
              onClick={() =>
                void act(async () => {
                  const w = await api.hireWorker(f.id);
                  return `Hired ${w.name} (level ${w.skillLevel}) at ${money(w.wagePerDay)}/day.`;
                })
              }
            >
              Hire
            </button>
          )}
        </span>
      </div>
      {f.workers.map((w) => (
        <div className="kv" key={w.id}>
          <span className="k">
            {w.name} · L{w.skillLevel} {w.skill}
          </span>
          <span className="v">
            {money(w.wagePerDay)}/day{" "}
            <button
              disabled={busy}
              onClick={() =>
                void act(async () => {
                  await api.dismissWorker(w.id);
                  return `${w.name} dismissed.`;
                })
              }
            >
              ×
            </button>
          </span>
        </div>
      ))}

      {orders.length > 0 && (
        <>
          <div className="field" style={{ marginTop: 8 }}>
            <label htmlFor={`order-${f.id}`}>Production</label>
            <select
              id={`order-${f.id}`}
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
            >
              <option value="">— idle —</option>
              {orders.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                  {o.inputs.length > 0
                    ? ` (${o.inputs.map((i) => `${i.qty} ${i.commodity}`).join(" + ")})`
                    : ""}
                  {" → "}
                  {o.outputs.map((i) => `${i.qty} ${i.commodity}`).join(" + ")}
                </option>
              ))}
            </select>
          </div>
          <div className="row-actions">
            <button
              disabled={busy || orderId === (f.job?.workOrderId ?? "")}
              onClick={() =>
                void act(async () => {
                  await api.setJob(f.id, orderId === "" ? null : orderId);
                  return orderId === "" ? "Production stopped." : "Production order set.";
                })
              }
            >
              Apply
            </button>
            {f.job && (
              <span className="small faint" style={{ alignSelf: "center" }}>
                {f.job.produced.toLocaleString()} produced so far
              </span>
            )}
          </div>
        </>
      )}

      {orders.some((o) => o.kind === "recipe") && (
        <PrepSection facility={f} orders={orders} busy={busy} act={act} />
      )}

      {f.isShop && (
        <>
          <div className="storefront-head">
            <span className="income">
              {money(f.estRevenuePerDay)}
              <span className="per">/game day</span>
            </span>
            <span className="appeal">{f.appeal.toFixed(2)} ✦ appeal</span>
          </div>
          <div className="kv">
            <span className="k">Cleanliness</span>
            <span className="v clean-meter">
              <span className="bar" aria-hidden>
                <span
                  className="fill"
                  style={{
                    width: `${Math.round(f.cleanliness)}%`,
                    background: f.cleanliness > 60 ? "var(--accent)" : f.cleanliness > 35 ? "var(--gold)" : "var(--down)",
                  }}
                />
              </span>
              {Math.round(f.cleanliness)}%
              {f.cleanliness < 99 && (
                <button
                  disabled={busy}
                  onClick={() =>
                    void act(async () => {
                      await api.wipeFacility(f.id);
                      return "Wiped down — spotless again.";
                    })
                  }
                >
                  Wipe down
                </button>
              )}
            </span>
          </div>
          <p className="small faint" style={{ margin: "2px 0 8px" }}>
            Serving customers wears the place down; a grubby counter turns them away. Menu breadth
            feeds appeal, foot traffic here is ×{f.busyness.toFixed(1)}.
          </p>

          <h3 style={{ marginTop: 10 }}>Menu &amp; prices</h3>
          {f.offers.map((o) => (
            <PriceRow key={o.commodity} facility={f} offer={o} busy={busy} act={act} />
          ))}
          <div className="field-row">
            <div className="field">
              <label htmlFor={`oc-${f.id}`}>Sell</label>
              <input
                id={`oc-${f.id}`}
                placeholder="e.g. BREAD"
                value={offerCommodity}
                onChange={(e) => setOfferCommodity(e.target.value.toUpperCase())}
              />
            </div>
            <div className="field">
              <label htmlFor={`op-${f.id}`}>at</label>
              <input id={`op-${f.id}`} value={offerPrice} onChange={(e) => setOfferPrice(e.target.value)} />
            </div>
          </div>
          <div className="row-actions">
            <button
              disabled={busy || offerCommodity.length === 0}
              onClick={() =>
                void act(async () => {
                  await api.setOffers(f.id, [
                    ...f.offers.map((x) => ({ commodity: x.commodity, price: x.price })),
                    { commodity: offerCommodity, price: normalizePrice(offerPrice) },
                  ]);
                  return `${offerCommodity} now on offer. Stock it via your inventory.`;
                })
              }
            >
              Add offer
            </button>
          </div>
        </>
      )}

      <div className="row-actions" style={{ marginTop: 8 }}>
        <button
          disabled={busy}
          onClick={() => {
            if (!window.confirm(`Remove ${f.typeName}? Nothing is refunded.`)) return;
            void act(async () => {
              await api.removeFacility(f.id);
              return `${f.typeName} removed.`;
            });
          }}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

function normalizePrice(text: string): string {
  const n = Number(text);
  if (!Number.isFinite(n) || n <= 0) return "1.00";
  return n.toFixed(2);
}

/**
 * One menu line: a price slider over the demand curve with the
 * revenue-optimal price marked. Dragging previews; releasing commits.
 */
function PriceRow({
  facility: f,
  offer: o,
  busy,
  act,
}: {
  facility: FacilitySummary;
  offer: FacilitySummary["offers"][number];
  busy: boolean;
  act: (fn: () => Promise<string>) => Promise<void>;
}): JSX.Element {
  const ref = Number(o.referencePrice);
  const optimal = Number(o.optimalPrice);
  const lo = Math.max(0.01, ref * 0.2);
  const hi = Math.max(lo + 0.01, ref * 3);
  const [value, setValue] = useState(Number(o.price));
  useEffect(() => setValue(Number(o.price)), [o.price]);

  const commit = (next: number): void => {
    if (next.toFixed(2) === Number(o.price).toFixed(2)) return;
    void act(async () => {
      await api.setOffers(
        f.id,
        f.offers.map((x) => ({
          commodity: x.commodity,
          price: x.commodity === o.commodity ? next.toFixed(2) : x.price,
        })),
      );
      return `${o.commodity.replace(/_/g, " ")} now ${money(next.toFixed(2))}.`;
    });
  };

  const ratio = optimal > 0 ? value / optimal : 1;
  const verdict = ratio < 0.85 ? "underpriced" : ratio <= 1.25 ? "sweet spot" : "scaring people off";

  return (
    <div className="price-row">
      <div className="kv">
        <span className="k">{o.commodity.replace(/_/g, " ")}</span>
        <span className="v">
          {money(value.toFixed(2))}{" "}
          <span className={`small ${ratio <= 1.25 && ratio >= 0.85 ? "up" : "faint"}`}>{verdict}</span>{" "}
          <button
            disabled={busy}
            title="Take off the menu"
            onClick={() =>
              void act(async () => {
                await api.setOffers(
                  f.id,
                  f.offers
                    .filter((x) => x.commodity !== o.commodity)
                    .map((x) => ({ commodity: x.commodity, price: x.price })),
                );
                return `${o.commodity} taken off the menu.`;
              })
            }
          >
            ×
          </button>
        </span>
      </div>
      <div className="price-slider">
        <span
          className="optimal"
          style={{ left: `${(100 * (optimal - lo)) / (hi - lo)}%` }}
          title={`Optimal ${money(optimal.toFixed(2))}`}
        />
        <input
          type="range"
          min={lo}
          max={hi}
          step={0.01}
          value={value}
          aria-label={`${o.commodity} price`}
          onChange={(e) => setValue(Number(e.target.value))}
          onPointerUp={() => commit(value)}
          onKeyUp={(e) => {
            if (e.key === "ArrowLeft" || e.key === "ArrowRight") commit(value);
          }}
        />
        <span className="ends">
          <span>{money(lo.toFixed(2))}</span>
          <span className="mid">optimal {money(optimal.toFixed(2))}</span>
          <span>{money(hi.toFixed(2))}</span>
        </span>
      </div>
    </div>
  );
}

/**
 * Hand-prep: a timing game. The needle sweeps; land a tap inside the green
 * window to bank one batch, and the window narrows as the streak grows. A
 * miss ends the run. Whatever was banked is sent to the server, which clamps
 * it to what the shelves actually hold.
 */
function PrepSection({
  facility: f,
  orders,
  busy,
  act,
}: {
  facility: FacilitySummary;
  orders: BusinessCatalogResponse["workOrders"];
  busy: boolean;
  act: (fn: () => Promise<string>) => Promise<void>;
}): JSX.Element {
  const recipes = useMemo(() => orders.filter((o) => o.kind === "recipe"), [orders]);
  const [orderId, setOrderId] = useState(recipes[0]?.id ?? "");
  const order = recipes.find((o) => o.id === orderId) ?? recipes[0] ?? null;

  const maxBatches = useMemo(() => {
    if (!order) return 0;
    let max = 40;
    for (const input of order.inputs) {
      const have = f.stock.find((s) => s.commodity === input.commodity)?.qty ?? 0;
      max = Math.min(max, Math.floor(have / input.qty));
    }
    return Math.max(0, max);
  }, [order, f.stock]);

  const [playing, setPlaying] = useState(false);
  const [banked, setBanked] = useState(0);

  const serve = (count: number): void => {
    setPlaying(false);
    setBanked(0);
    if (count <= 0 || !order) return;
    void act(async () => {
      const r = await api.prepFacility(f.id, order.id, count);
      return `Prepped ${r.prepped} batch${r.prepped === 1 ? "" : "es"} of ${order.name.toLowerCase()}.`;
    });
  };

  return (
    <div className="prep">
      {!playing ? (
        <div className="row-actions">
          {recipes.length > 1 && (
            <select
              aria-label="Recipe to prep"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
            >
              {recipes.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          )}
          <button
            className="primary"
            disabled={busy || maxBatches === 0}
            onClick={() => {
              setBanked(0);
              setPlaying(true);
            }}
          >
            Prep by hand
          </button>
          <span className="small faint" style={{ alignSelf: "center" }}>
            {maxBatches === 0
              ? "no ingredients on the shelves"
              : `ingredients for ${maxBatches} batch${maxBatches === 1 ? "" : "es"}`}
          </span>
        </div>
      ) : (
        <PrepGame
          cap={maxBatches}
          banked={banked}
          onHit={() => setBanked((n) => n + 1)}
          onDone={(count) => serve(count)}
        />
      )}
    </div>
  );
}

function PrepGame({
  cap,
  banked,
  onHit,
  onDone,
}: {
  cap: number;
  banked: number;
  onHit: () => void;
  onDone: (banked: number) => void;
}): JSX.Element {
  const needleRef = useRef<HTMLSpanElement>(null);
  const posRef = useRef(0);
  // The window drifts and narrows as the streak grows.
  const [win, setWin] = useState({ centre: 0.55, width: 0.26 });

  useEffect(() => {
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number): void => {
      // Triangle sweep, slightly faster the more is banked.
      const period = Math.max(700, 1500 - banked * 55);
      const phase = ((now - t0) % (period * 2)) / period;
      const pos = phase < 1 ? phase : 2 - phase;
      posRef.current = pos;
      if (needleRef.current) needleRef.current.style.left = `${(pos * 100).toFixed(2)}%`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [banked]);

  const tap = (): void => {
    const pos = posRef.current;
    const hit = Math.abs(pos - win.centre) <= win.width / 2;
    if (!hit) {
      onDone(banked); // a slip ends the run; whatever is banked still counts
      return;
    }
    onHit();
    if (banked + 1 >= cap) {
      onDone(banked + 1);
      return;
    }
    setWin({
      centre: 0.2 + 0.6 * Math.random(),
      width: Math.max(0.09, 0.26 - 0.015 * (banked + 1)),
    });
  };

  return (
    <div className="prep-game">
      <div className="kv">
        <span className="k">
          {banked} / {cap} banked
        </span>
        <span className="v small faint">tap in the green — a slip serves what you have</span>
      </div>
      <button type="button" className="prep-bar" onClick={tap} aria-label="Grill">
        <span
          className="window"
          style={{ left: `${(win.centre - win.width / 2) * 100}%`, width: `${win.width * 100}%` }}
        />
        <span ref={needleRef} className="needle" />
      </button>
      <div className="row-actions">
        <button onClick={() => onDone(banked)}>Serve {banked > 0 ? banked : "nothing"}</button>
      </div>
    </div>
  );
}

const LIVESTOCK_KINDS = [
  { id: "cow", label: "Cows" },
  { id: "pig", label: "Pigs" },
  { id: "sheep", label: "Sheep" },
  { id: "chicken", label: "Chickens" },
];

function HerdSection({
  facility: f,
  busy,
  act,
}: {
  facility: FacilitySummary;
  busy: boolean;
  act: (fn: () => Promise<string>) => Promise<void>;
}): JSX.Element {
  const [kind, setKind] = useState("chicken");
  const [heads, setHeads] = useState(4);
  return (
    <>
      <div className="kv">
        <span className="k">Herd</span>
        <span className="v">
          {f.herd.length === 0
            ? "empty pens"
            : f.herd.map((h) => `${h.heads} ${h.kind}`).join(" · ")}
        </span>
      </div>
      <div className="field-row">
        <div className="field">
          <label htmlFor={`lk-${f.id}`}>Buy animals</label>
          <select id={`lk-${f.id}`} value={kind} onChange={(e) => setKind(e.target.value)}>
            {LIVESTOCK_KINDS.map((k) => (
              <option key={k.id} value={k.id}>
                {k.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor={`lh-${f.id}`}>Heads</label>
          <input
            id={`lh-${f.id}`}
            type="number"
            min={1}
            value={heads}
            onChange={(e) => setHeads(Math.max(1, Number(e.target.value) || 1))}
          />
        </div>
      </div>
      <div className="row-actions">
        <button
          disabled={busy}
          onClick={() =>
            void act(async () => {
              await api.buyLivestock(f.id, kind, heads);
              return `${heads} ${kind}(s) delivered to the barn.`;
            })
          }
        >
          Buy livestock
        </button>
        <span className="small faint" style={{ alignSelf: "center" }}>
          Animal products need the herd; feed comes from barn stock.
        </span>
      </div>
    </>
  );
}

function LogisticsSection({
  catalog,
  facilities,
  busy,
  act,
}: {
  catalog: BusinessCatalogResponse | null;
  facilities: FacilitySummary[];
  busy: boolean;
  act: (fn: () => Promise<string>) => Promise<void>;
}): JSX.Element {
  const notify = useApp((s) => s.notify);
  const [fleet, setFleet] = useState<LogisticsResponse | null>(null);
  const [vehicleClass, setVehicleClass] = useState("cargo_bicycle");
  const [fromId, setFromId] = useState<string>("");
  const [toId, setToId] = useState<string>("");
  const [commodity, setCommodity] = useState("POTATO");

  const reload = async (): Promise<void> => {
    try {
      setFleet(await api.logistics());
    } catch (err) {
      notify("err", describe(err));
    }
  };
  useEffect(() => {
    void reload();
    const t = setInterval(() => void reload(), 15_000);
    return () => clearInterval(t);
  }, []);

  const wrapped = async (fn: () => Promise<string>): Promise<void> => {
    await act(fn);
    await reload();
  };

  const endpoints = [
    { id: "", label: "Holdings (home)" },
    ...facilities.map((f) => ({ id: f.id, label: `${f.typeName} — ${f.label}` })),
  ];
  const unassigned = (fleet?.vehicles ?? []).filter((v) => v.laneId === null);

  return (
    <>
      <h3>Fleet</h3>
      <div className="card">
        {(fleet?.vehicles ?? []).length === 0 ? (
          <p className="empty">No vehicles. A cargo bicycle is the cheapest way to move goods.</p>
        ) : (
          (fleet?.vehicles ?? []).map((v) => (
            <div className="kv" key={v.id}>
              <span className="k">{v.name}</span>
              <span className="v">
                {v.laneId ? "on a lane" : "idle"}{" "}
                <button
                  disabled={busy}
                  onClick={() =>
                    void wrapped(async () => {
                      await api.sellVehicle(v.id);
                      return `${v.name} retired.`;
                    })
                  }
                >
                  ×
                </button>
              </span>
            </div>
          ))
        )}
        <div className="field-row">
          <div className="field">
            <label htmlFor="vclass">Buy vehicle</label>
            <select id="vclass" value={vehicleClass} onChange={(e) => setVehicleClass(e.target.value)}>
              {(catalog?.vehicleClasses ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} — {money(v.price)} · {v.cargo} units
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="row-actions">
          <button
            disabled={busy}
            onClick={() =>
              void wrapped(async () => {
                await api.buyVehicle(vehicleClass);
                return "Vehicle delivered. Assign it to a lane.";
              })
            }
          >
            Buy
          </button>
        </div>
      </div>

      <h3>Lanes</h3>
      {(fleet?.lanes ?? []).map((lane) => (
        <div className="card" key={lane.id}>
          <div className="kv">
            <span className="k" style={{ fontWeight: 700, color: "var(--ink)" }}>
              {lane.from.label} → {lane.to.label}
            </span>
            <span className="pill-tag">{lane.commodity}</span>
          </div>
          <div className="kv">
            <span className="k">
              {lane.distanceM === null ? "—" : `${(lane.distanceM / 1000).toFixed(1)} km`} ·{" "}
              {lane.vehicles.length} vehicle(s), {lane.drivers.length} driver(s)
            </span>
            <span className="v">
              ~{lane.estUnitsPerDay.toLocaleString()}/day · moved {lane.movedTotal.toLocaleString()}
            </span>
          </div>
          {lane.drivers.map((d) => (
            <div className="kv" key={d.id}>
              <span className="k">
                {d.name} · L{d.skillLevel} driver
              </span>
              <span className="v">
                {money(d.wagePerDay)}/day{" "}
                <button
                  disabled={busy}
                  onClick={() =>
                    void wrapped(async () => {
                      await api.dismissWorker(d.id);
                      return `${d.name} dismissed.`;
                    })
                  }
                >
                  ×
                </button>
              </span>
            </div>
          ))}
          <div className="row-actions">
            {unassigned.length > 0 && (
              <button
                disabled={busy}
                onClick={() =>
                  void wrapped(async () => {
                    await api.assignVehicle(unassigned[0]!.id, lane.id);
                    return `${unassigned[0]!.name} assigned.`;
                  })
                }
              >
                Assign {unassigned[0]!.name}
              </button>
            )}
            {lane.drivers.length < Math.max(1, lane.vehicles.length) && (
              <button
                disabled={busy}
                onClick={() =>
                  void wrapped(async () => {
                    const d = await api.hireDriver(lane.id);
                    return `Hired ${d.name} at ${money(d.wagePerDay)}/day.`;
                  })
                }
              >
                Hire driver
              </button>
            )}
            <button
              disabled={busy}
              onClick={() =>
                void wrapped(async () => {
                  await api.deleteLane(lane.id);
                  return "Lane removed.";
                })
              }
            >
              Remove lane
            </button>
          </div>
        </div>
      ))}

      <h3>New lane</h3>
      <div className="card">
        <div className="field-row">
          <div className="field">
            <label htmlFor="lfrom">From</label>
            <select id="lfrom" value={fromId} onChange={(e) => setFromId(e.target.value)}>
              {endpoints.map((p) => (
                <option key={p.id || "holdings"} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="lto">To</label>
            <select id="lto" value={toId} onChange={(e) => setToId(e.target.value)}>
              {endpoints.map((p) => (
                <option key={p.id || "holdings"} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="field">
          <label htmlFor="lcom">Commodity</label>
          <input
            id="lcom"
            value={commodity}
            onChange={(e) => setCommodity(e.target.value.toUpperCase())}
          />
        </div>
        <button
          className="primary"
          style={{ width: "100%" }}
          disabled={busy || fromId === toId}
          onClick={() =>
            void wrapped(async () => {
              await api.createLane(fromId === "" ? null : fromId, toId === "" ? null : toId, commodity);
              return "Lane created. Assign a vehicle and hire a driver to start it moving.";
            })
          }
        >
          Create lane
        </button>
        <p className="notice info" style={{ margin: "9px 0 0" }}>
          Lanes haul one commodity between your Holdings and your facilities. Throughput comes from
          vehicle cargo, speed and distance; watch them run on the map's Logistics overlay.
        </p>
      </div>
    </>
  );
}

function InstallSection({
  catalog,
  busy,
  act,
}: {
  catalog: BusinessCatalogResponse | null;
  busy: boolean;
  act: (fn: () => Promise<string>) => Promise<void>;
}): JSX.Element {
  const [typeId, setTypeId] = useState("food_cart");
  const [parcels, setParcels] = useState<PortfolioResponse["parcels"]>([]);
  const [units, setUnits] = useState<
    Array<{ id: string; label: string; use: string; buildingLabel: string; tenure: string }>
  >([]);
  const [parcelId, setParcelId] = useState("");
  const [unitId, setUnitId] = useState("");

  useEffect(() => {
    api.portfolio().then((r) => setParcels(r.parcels), () => undefined);
    api.myUnits().then((r) => setUnits(r.units), () => undefined);
  }, []);

  const type = catalog?.facilityTypes.find((t) => t.id === typeId) ?? null;
  const eligibleUnits = useMemo(
    () => (type ? units.filter((u) => type.unitUses.includes(u.use)) : []),
    [type, units],
  );

  return (
    <>
      <div className="field">
        <label htmlFor="ftype">Facility</label>
        <select id="ftype" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
          {(catalog?.facilityTypes ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} — {money(t.installCost)}
            </option>
          ))}
        </select>
      </div>

      {type && (
        <div className="card">
          <div className="kv">
            <span className="k">Install cost</span>
            <span className="v">{money(type.installCost)}</span>
          </div>
          <div className="kv">
            <span className="k">Upkeep / game day</span>
            <span className="v">{money(type.upkeepPerDay)}</span>
          </div>
          <div className="kv">
            <span className="k">Worker slots</span>
            <span className="v">
              {type.workerSlots} · {type.skill}
            </span>
          </div>
          <div className="kv">
            <span className="k">Goes</span>
            <span className="v">
              {type.mobile
                ? "anywhere (permit)"
                : type.requiresLand
                  ? "on owned land"
                  : `in a ${type.unitUses.join("/")} unit`}
            </span>
          </div>
        </div>
      )}

      {type?.requiresLand && (
        <div className="field">
          <label htmlFor="fparcel">On parcel</label>
          <select id="fparcel" value={parcelId} onChange={(e) => setParcelId(e.target.value)}>
            <option value="">— choose owned land —</option>
            {parcels.map((p) => (
              <option key={p.id} value={p.id}>
                {p.landUse} · {Math.round(p.areaM2)} m² · {p.chunkId}
              </option>
            ))}
          </select>
        </div>
      )}

      {type && !type.requiresLand && !type.mobile && (
        <div className="field">
          <label htmlFor="funit">In unit</label>
          <select id="funit" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
            <option value="">— choose a unit you own or lease —</option>
            {eligibleUnits.map((u) => (
              <option key={u.id} value={u.id}>
                {u.buildingLabel} · {u.label} ({u.use}, {u.tenure})
              </option>
            ))}
          </select>
        </div>
      )}

      {type?.mobile && (
        <p className="notice info">
          The cart is placed at the map's current position — pan to a busy street first. Foot
          traffic where it stands is what brings customers.
        </p>
      )}

      <button
        className="primary"
        style={{ width: "100%" }}
        disabled={
          busy ||
          !type ||
          (type.requiresLand && parcelId === "") ||
          (!type.requiresLand && !type.mobile && unitId === "")
        }
        onClick={() => {
          if (!type) return;
          void act(async () => {
            const input: Parameters<typeof api.installFacility>[0] = { facilityType: type.id };
            if (type.requiresLand) input.parcelId = parcelId;
            else if (type.mobile) {
              const world = (window as { __pbWorld?: { cameraLngLat(): [number, number] } }).__pbWorld;
              const c = world?.cameraLngLat();
              if (!c) throw new Error("move the map to where the cart should stand");
              input.lon = c[0];
              input.lat = c[1];
            } else input.unitId = unitId;
            await api.installFacility(input);
            return `${type.name} installed for ${money(type.installCost)}. Hire staff to get it moving.`;
          });
        }}
      >
        Install{type ? ` ${type.name.toLowerCase()} for ${money(type.installCost)}` : ""}
      </button>
    </>
  );
}

/* ------------------------------------------------------------------ */

export function OffersWindow(): JSX.Element {
  const chrome = useWindowChrome("offers");
  const notify = useApp((s) => s.notify);
  const [offers, setOffers] = useState<PropertyOfferSummary[] | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = async (): Promise<void> => {
    try {
      const r = await api.propertyOffers();
      setOffers(r.offers);
    } catch (err) {
      notify("err", describe(err));
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const act = async (fn: () => Promise<string>): Promise<void> => {
    setBusy(true);
    try {
      notify("ok", await fn());
      await reload();
      useApp.getState().setDashboard(await api.dashboard());
    } catch (err) {
      notify("err", describe(err));
      await reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Window title="Property offers" initial={{ x: 260, y: 150 }} width={520} maxHeight={480} {...chrome}>
      {offers === null ? (
        <p className="empty">Loading…</p>
      ) : offers.length === 0 ? (
        <p className="empty">
          No open offers. Make one from the inspector on any property another player owns — your
          money is held in escrow until they answer.
        </p>
      ) : (
        <table>
          <thead>
            <tr>
              <th></th>
              <th>Property</th>
              <th>With</th>
              <th className="num">Amount</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {offers.map((o) => (
              <tr key={o.id}>
                <td>
                  <span className={`pill-tag${o.direction === "incoming" ? " mine" : ""}`}>
                    {o.direction === "incoming" ? "to you" : "yours"}
                  </span>
                </td>
                <td>{o.propertyLabel}</td>
                <td className="faint">{o.counterpartyName}</td>
                <td className="num">{money(o.amount)}</td>
                <td className="num">
                  {o.direction === "incoming" ? (
                    <>
                      <button
                        disabled={busy}
                        onClick={() =>
                          void act(async () => {
                            const r = await api.acceptOffer(o.id);
                            return `Sold for ${money(r.amount)}.`;
                          })
                        }
                      >
                        Accept
                      </button>{" "}
                      <button
                        disabled={busy}
                        onClick={() =>
                          void act(async () => {
                            await api.declineOffer(o.id);
                            return "Offer declined.";
                          })
                        }
                      >
                        Decline
                      </button>
                    </>
                  ) : (
                    <button
                      disabled={busy}
                      onClick={() =>
                        void act(async () => {
                          await api.withdrawOffer(o.id);
                          return "Offer withdrawn; escrow released.";
                        })
                      }
                    >
                      Withdraw
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Window>
  );
}
